<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

defineProps(['model'])

const titleFor = useVideoIframeTitle()
function videoTitle(item: { label?: string, videoLink?: { href?: string } }) {
  return titleFor([item?.label], item?.videoLink?.href)
}

const screenWidth = ref(0)

function updateScreenWidth() {
  screenWidth.value = window.innerWidth
}

onMounted(() => {
  updateScreenWidth()
  window.addEventListener('resize', updateScreenWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenWidth)
})
</script>

<template>
  <div class="video-listing-block block-spacing">
    <div class="container-fluid">
      <div
        class="video-listing-block__accordion-wrap"
      >
        <div class="block-spacing">
          <div class="prose mb-5 lg:mb-7.5">
            <h2 v-if="model?.blockTitle?.value">
              {{ model.blockTitle.value }}
            </h2>
          </div>
          <UAccordion
            type="single"
            :items="model?.items?.expandedValue?.map(item => {
              return {
                label: item?.label?.value,
                content: item?.content?.value,
                mainImage: item?.aprimoMainImage,
                videoLink: item?.videoLink?.value,
                altText: item?.imageAltText?.value,
              }
            })"
            :ui="{
              trigger: 'w-full flex items-center relative',
              label: 'truncate',
            }"
          >
            <template #body="{ item }">
              <iframe
                v-if="item?.videoLink?.href"
                :src="item.videoLink.href"
                :title="videoTitle(item)"
                allow="autoplay; encrypted-media;"
                allowfullscreen
                class="aspect-video w-full video-block__video rounded-xl"
                loading="lazy"
              />
              <NuxtImg
                v-else-if="item.mainImage?.url"
                provider="fastlyAprimo"
                :src="item?.mainImage?.url"
                loading="lazy"
                :alt="item.altText ?? ''"
                :width="item?.mainImage?.width"
                :height="item?.mainImage?.height"
                fit="cover"
                sizes="(max-width: 768px) 100vw, 720px"
                class="mt-0 w-full aspect-video object-cover rounded-xl"
              />
              <div
                class="accordion-body"
                v-html="item?.content"
              />
            </template>
          </UAccordion>
        </div>
      </div>
      <div
        class="video-listing-block__tabs-wrap w-full prose"
      >
        <BlockHeader
          :title="model?.blockTitle?.value"
        />
        <UTabs
          :items="model?.items?.expandedValue?.map(item => {
            return {
              label: item?.label?.value,
              content: item?.content?.value,
              mainImage: item?.aprimoMainImage,
              videoLink: item?.videoLink?.value,
              altText: item?.imageAltText?.value,
            }
          })"
          class="w-full"
          orientation="vertical"
          color="ghost"
          variant="pill"
          :ui="{ label: 'min-w-0' }"
        >
          <template #content="{ item }">
            <iframe
              v-if="item?.videoLink?.href"
              :src="item.videoLink.href"
              :title="videoTitle(item)"
              frameborder="0"
              allow="autoplay; encrypted-media;"
              allowfullscreen
              class="aspect-video w-full video-block__video rounded-xl p-5 pb-0"
              loading="lazy"
            />
            <NuxtImg
              v-else-if="item?.mainImage.url"
              provider="fastlyAprimo"
              :src="item?.mainImage?.url"
              loading="lazy"
              :alt="item.altText ?? ''"
              :width="item?.mainImage?.width"
              :height="item?.mainImage?.height"
              fit="cover"
              sizes="(max-width: 768px) 100vw, 720px"
              class="mt-0 w-full aspect-video object-cover rounded-xl p-5 pb-0"
            />
            <div
              class="pt-9 px-8.5 pb-12.5 rounded-xl"
              v-html="item?.content"
            />
          </template>
        </UTabs>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/css/blocks/video-listing-block.scss';
</style>
